import { createSelector } from 'reselect'

import { selectActualEnabledLanguages } from 'containers/App/selectors'

import {
  CAMPAIGN_NOTIFICATION_TYPE,
  CAMPAIGN_TYPES,
  createWizardCampaignSelector,
  getCampaignTypeByState,
} from '../../constants'
import { selectDefaultCampaignFailedAlertContent } from '../../selectors'

import { SENDING_VALUES } from './constants'

const selfPath = (state) => createWizardCampaignSelector(state).self
export const selectLoader = (state) => selfPath(state).loader
export const selectError = (state) => selfPath(state).error
export const selectValidation = (state) => selfPath(state).validation
export const selectDraftLoader = (state) => selfPath(state).draftLoader
export const selectDraftError = (state) => selfPath(state).draftError
export const selectDraftGotManaged = (state) => selfPath(state).draftGotManaged
export const selectIsQuickLaunch = (state) => selfPath(state).isQuickLaunch
export const selectGotApproved = (state) => selfPath(state).gotApproved
export const selectControlsMessage = (state) => selfPath(state).controlsMessage
export const selectCampaignId = (state) => selfPath(state).campaign.id
export const selectName = (state) => selfPath(state).campaign.name
export const selectDefaultLocaleId = (state) => selfPath(state).campaign.defaultLocaleId
export const selectParticipants = (state) => selfPath(state).campaign.participants
export const selectTimezone = (state) => selfPath(state).campaign.timezone
export const selectMaxEmailPerDay = (state) => selfPath(state).campaign.maxEmailPerDay
export const selectLaunchDate = (state) => selfPath(state).campaign.launchDate
export const selectLaunchTime = (state) => selfPath(state).campaign.launchTime
export const selectEndDate = (state) => selfPath(state).campaign.endDate
export const selectCloseTime = (state) => selfPath(state).campaign.closeTime
export const selectSendRemindersUntil = (state) => selfPath(state).campaign.sendRemindersUntil
export const selectRandomized = (state) => selfPath(state).campaign.randomized
export const selectSendAllWeek = (state) => selfPath(state).campaign.sendAllWeek
export const selectSendingDays = (state) => selfPath(state).campaign.sendingDays
export const selectSendReminderEveryXBusinessDays = (state) =>
  selfPath(state).campaign.sendReminderEveryXBusinessDays
export const selectSelectedDelivery = (state) => selfPath(state).campaign.delivery
export const selectCampaignInProgressStatus = (state) => selfPath(state).campaignInProgressError
export const selectIs12HoursLocalFormat = (state) => /(US|America)/.test(selectTimezone(state))
export const selectSelectedScenarios = (state) => selfPath(state).campaign.selectedScenarios
export const selectScenariosList = createSelector(selectSelectedScenarios, (selectedScenarios) =>
  selectedScenarios?.map((scenario) => ({
    value: scenario.id,
    label: scenario.name,
    contents: scenario.contents,
  }))
)
export const selectSendingType = (state) => {
  if (selfPath(state).campaign.randomizedScenarios) return SENDING_VALUES.RANDOMIZED
  else if (selfPath(state).campaign.leveledScenarios) return SENDING_VALUES.LEVELED
}
export const selectSelectedLandingPage = (state) => selfPath(state).campaign.selectedLandingPage
export const selectSelectedTrainingVideo = (state) => selfPath(state).campaign.selectedTrainingVideo
export const selectIsResetCampaign = (state) =>
  !!(selectParticipants(state).items?.length || selectSelectedScenarios(state)?.length)
export const selectSpearPhishingCampaignData = (state) =>
  selfPath(state).campaign.spearPhishingCampaignData
export const selectGeneratedEmailsParticipants = (state) =>
  selfPath(state).campaign.generatedEmailsParticipants
export const selectGeneratedEmailsParticipantsLoader = (state) =>
  selfPath(state).campaign.generatedEmailsParticipantsLoader
export const selectSendCampaignFailedEmail = (state) =>
  selfPath(state).campaign.sendCampaignFailedEmail
export const selectSendCampaignSummaryToCampaignReportRecipients = (state) =>
  selfPath(state).campaign.sendCampaignSummaryToCampaignReportRecipients
export const selectSharedMailboxWasExcluded = (state) =>
  selfPath(state).campaign.sharedMailboxWasExcluded
export const selectSendCompletionEmail = (state) => selfPath(state).campaign.sendCompletionEmail
export const selectTrainingReminders = (state) => selfPath(state).campaign.trainingReminders
export const selectManagerReportRemindersThreshold = (state) =>
  selfPath(state).campaign.managerReportRemindersThreshold
export const selectSendManagerReportEveryXDays = (state) =>
  selfPath(state).campaign.sendManagerReportEveryXDays
export const selectManagerReportDayOfWeek = (state) =>
  selfPath(state).campaign.managerReportDayOfWeek
export const selectManagerNotificationBannerContent = (state) =>
  selfPath(state).campaign.managerNotificationBannerContent
export const selectManagerReportEmailContent = (state) =>
  selfPath(state).campaign.managerReportEmailContent
export const selectManagerReportEmailContentSubject = (state) =>
  selfPath(state).campaign.managerReportEmailContentSubject
export const selectCampaignManagerNotificationState = (state) =>
  selfPath(state).campaign.campaignManagerNotificationState

export const selectDefaultFailedEmailContent = (state) => {
  const defaultLocaleId = selectDefaultLocaleId(state)
  const defaultContent = selectDefaultCampaignFailedAlertContent(state)
  if (!defaultLocaleId || !defaultContent) return {}

  const relevantShortName = selectActualEnabledLanguages(state)
    .find(({ id }) => id == defaultLocaleId)
    .short_name.toLowerCase()

  return defaultContent[relevantShortName]
}

/**
 * Returns the content from the campaign itself (if the default was modified),
 * or the default version from the campaign settings.
 * For non SIMULATION/SP campaigns, returns null.
 *
 * @returns {{subject: string, body: string} | null} - The content object with subject and body, or null if not applicable.
 */
export const selectRelevantFailedEmailContent = (state) => {
  if (
    [CAMPAIGN_TYPES.SIMULATION, CAMPAIGN_TYPES.SPEAR_PHISHING].includes(
      getCampaignTypeByState(state)
    )
  ) {
    const modifiedContent = selfPath(state).campaign.notifications.find(
      (notification) => notification.type === CAMPAIGN_NOTIFICATION_TYPE.CAMPAIGN_FAILED
    )
    if (modifiedContent) return modifiedContent
    else return selectDefaultFailedEmailContent(state)
  }

  return null
}
